import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { styleConfig } from '@/config/config';
import { border, layout, letterSpacing, shadow, transition } from '@/config/tokens';
import { mobileTokens } from '@/config/mobileTokens';
import { uppercaseActionTypography } from '@/config/typography';

const defaultTheme = createTheme({
  breakpoints: {
    values: { xs: 300, sm: 480, md: 768, lg: 1200, xl: 1600 },
  },
});

// Deterministic SSR media-query matcher: assume desktop (>= lg) on the server
const createSsrMatchMedia = (desktopWidth: number) => (query: string) => {
  const min = /\(\s*min-width\s*:\s*([0-9.]+)px\s*\)/.exec(query);
  const max = /\(\s*max-width\s*:\s*([0-9.]+)px\s*\)/.exec(query);
  let matches = true;
  if (min) {
    const minPx = parseFloat(min[1]);
    matches = matches && desktopWidth >= minPx;
  }
  if (max) {
    const maxPx = parseFloat(max[1]);
    matches = matches && desktopWidth <= maxPx;
  }
  if (!min && !max) {
    matches = false;
  }
  return { matches } as { matches: boolean };
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 300,
      sm: 480,
      md: 768,
      lg: 1200,
      xl: 1600,
    },
  },
  shape: { borderRadius: 0 },
  palette: {
    primary: {
      main: styleConfig.color.bg.gold,
      dark: styleConfig.color.bg.darkgrey,
    },
    secondary: {
      main: styleConfig.color.bg.black,
    },
    info: {
      main: styleConfig.color.text.gold,
      light: styleConfig.color.bg.warmLight,
      dark: styleConfig.color.text.gold,
      contrastText: styleConfig.color.text.white,
    },
  },
  typography: {
    htmlFontSize: 10,
    fontFamily: 'Open Sans',
    button: {
      ...uppercaseActionTypography,
      fontSize: 14,
      lineHeight: 1.25,
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          overflowX: 'clip',
          ...(ownerState.disableGutters && {
            paddingLeft: 0,
            paddingRight: 0,
          }),
          ...(!ownerState.disableGutters && {
            paddingLeft: layout.pageGutter.compact,
            paddingRight: layout.pageGutter.compact,
            '@media (min-width: 480px)': {
              paddingLeft: layout.pageGutter.standard,
              paddingRight: layout.pageGutter.standard,
            },
            [theme.breakpoints.up('lg')]: {
              paddingLeft: layout.pageGutter.wide,
              paddingRight: layout.pageGutter.wide,
            },
          }),
        }),
        maxWidthXl: {
          maxWidth: `${layout.contentMaxWidth}px !important`,
        },
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        // Single source of truth for SSR behaviour: use deterministic desktop match
        noSsr: false,
        ssrMatchMedia: createSsrMatchMedia(1200),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // MUI reapplies `min-height: auto` to menu items above its compact
          // breakpoint. Keep every option at the shared touch-target size.
          minHeight: '44px !important',
          paddingTop: 8,
          paddingBottom: 8,
          fontFamily: styleConfig.fonts.body,
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: -2,
            backgroundColor: styleConfig.color.bg.offwhite,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          // MUI's default 9px wrapper padding makes otherwise compact text
          // fields 56px tall. Four pixels preserves the input line box and
          // brings single and multi-value autocomplete fields onto the shared
          // 48px field rhythm.
          padding: 4,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: 15,
          fontSize: 13,
          lineHeight: 1.5,
          maxWidth: `min(350px, calc(100vw - ${layout.pageGutter.compact * 2}px))`,
          boxSizing: 'border-box',
          backgroundColor: styleConfig.color.bg.darkgrey,
        },
        arrow: {
          color: styleConfig.color.bg.darkgrey,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          width: mobileTokens.control.height.panel,
          height: mobileTokens.control.height.panel,
          minWidth: mobileTokens.control.height.panel,
          minHeight: mobileTokens.control.height.panel,
          borderRadius: 0,
          transition: `background-color ${transition.fast}, color ${transition.fast}, border-color ${transition.fast}`,
          '&:hover': {
            backgroundColor:
              ownerState.color === 'inherit'
                ? 'color-mix(in srgb, currentColor 12%, transparent)'
                : styleConfig.color.bg.offwhite,
            ...(ownerState.color === 'default' && { color: styleConfig.color.text.gold }),
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: 2,
          },
          '&.Mui-disabled': {
            color: styleConfig.color.text.grayOnOffwhite,
          },
        }),
        sizeSmall: {
          width: mobileTokens.control.height.compact,
          height: mobileTokens.control.height.compact,
          minWidth: mobileTokens.control.height.compact,
          minHeight: mobileTokens.control.height.compact,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        color: 'primary',
      },
      styleOverrides: {
        root: {
          width: mobileTokens.control.height.panel,
          height: mobileTokens.control.height.panel,
          padding: 10,
          borderRadius: 0,
          color: styleConfig.color.text.grayOnOffwhite,
          '&.Mui-checked': {
            color: styleConfig.color.bg.gold,
          },
          '&.Mui-disabled': {
            color: styleConfig.color.text.grayOnOffwhite,
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: 0,
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        color: 'primary',
      },
      styleOverrides: {
        root: {
          width: mobileTokens.control.height.panel,
          height: mobileTokens.control.height.panel,
          padding: 10,
          color: styleConfig.color.text.grayOnOffwhite,
          '&.Mui-checked': {
            color: styleConfig.color.bg.gold,
          },
          '&.Mui-disabled': {
            color: styleConfig.color.text.grayOnOffwhite,
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: 0,
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
        color: 'primary',
      },
      styleOverrides: {
        root: {
          width: 64,
          height: mobileTokens.control.height.panel,
          padding: 0,
          overflow: 'visible',
        },
        switchBase: {
          padding: 12,
          '&.Mui-checked': {
            color: styleConfig.color.bg.gold,
            '& + .MuiSwitch-track': {
              backgroundColor: styleConfig.color.bg.gold,
            },
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: -2,
          },
        },
        track: {
          position: 'absolute',
          top: 15,
          right: 12,
          bottom: 15,
          left: 12,
          width: 'auto',
          height: 'auto',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          paddingTop: 10,
          paddingBottom: 10,
          '@media (forced-colors: active)': {
            forcedColorAdjust: 'none',
            color: 'Highlight',
            '& .MuiSlider-rail': {
              backgroundColor: 'Canvas',
              border: '1px solid ButtonText',
              opacity: 1,
            },
            '& .MuiSlider-track': {
              backgroundColor: 'Highlight',
              borderColor: 'Highlight',
            },
            '& .MuiSlider-thumb': {
              backgroundColor: 'Canvas',
              border: '2px solid ButtonText',
            },
            '& .MuiSlider-thumb.Mui-focusVisible': {
              outline: '3px solid Highlight',
              outlineOffset: 2,
            },
          },
        },
        markLabel: {
          fontSize: 11,
          fontWeight: 600,
          color: styleConfig.color.text.grayOnOffwhite, // #6e6e6e — passes WCAG AA (4.82:1 on #F8F8F8)
        },
        markLabelActive: {
          fontWeight: 600,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        inputMultiline: {
          paddingTop: 12,
        },
        root: {
          fontWeight: 600,
        },
        input: {
          fontSize: 16,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: 0,
          ...(!ownerState.multiline && {
            minHeight:
              ownerState.size === 'small'
                ? mobileTokens.control.height.panel
                : mobileTokens.control.height.field,
          }),
        }),
        input: {
          paddingTop: 12,
          paddingBottom: 12,
        },
        inputMultiline: {
          padding: 0,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          paddingTop: 12,
          paddingBottom: 12,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        formControl: {
          marginTop: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          fontSize: 14,
          fontWeight: 800,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        fullWidth: {
          position: 'relative',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: styleConfig.color.text.darkgrey,
          fontSize: 14,
          fontWeight: 600,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.4,
          marginTop: 6,
        },
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontFamily: 'Bai Jamjuree',
          fontSize: 65,
          fontWeight: 600,
          color: styleConfig.color.text.darkgrey,
          // Every page title and hero heading is uppercase (Tom, 16 Sep 2026):
          // set once here rather than per call site. Section headings (h2+)
          // stay Title Case per WEB-94.
          textTransform: 'uppercase',

          [defaultTheme.breakpoints.down('lg')]: {
            fontSize: 45,
          },

          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: 32,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
          },
        },
        h2: {
          fontFamily: 'Bai Jamjuree',
          fontSize: 45,
          fontWeight: 600,
          color: styleConfig.color.text.darkgrey,

          [defaultTheme.breakpoints.down('md')]: {
            fontSize: 36,
          },
          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: 28,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
          },
        },
        h3: {
          fontFamily: 'Bai Jamjuree',
          fontSize: 36,
          fontWeight: 500,
          color: styleConfig.color.text.darkgrey,

          [defaultTheme.breakpoints.down('md')]: {
            fontSize: 28,
          },
          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: 24,
            lineHeight: 1.2,
          },
        },
        h4: {
          fontFamily: 'Bai Jamjuree',
          fontSize: 30,
          fontWeight: 500,
          color: styleConfig.color.text.darkgrey,
          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: 22,
            lineHeight: 1.25,
          },
        },
        h5: {
          fontFamily: 'Bai Jamjuree',
          fontSize: 24,
          fontWeight: 400,
          color: styleConfig.color.text.darkgrey,
        },
        h6: {
          fontFamily: 'Bai Jamjuree',
          fontSize: 20,
          fontWeight: 400,
          color: styleConfig.color.text.darkgrey,
        },
        body1: {
          fontSize: 16,
          color: styleConfig.color.text.darkgrey,

          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: 14,
          },
        },
        body2: {
          fontSize: 18,
          color: styleConfig.color.text.darkgrey,
        },
        caption: {
          fontSize: 12,
          color: styleConfig.color.text.gray,
          lineHeight: 1.4,
        },
        overline: {
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase' as const,
          color: styleConfig.color.text.gray,
          lineHeight: 1.5,
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          [defaultTheme.breakpoints.down('sm')]: {
            padding: '10px 5px',
            margin: 0,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: styleConfig.color.bg.altLightGrey,
          '& .MuiStepIcon-text': {
            fill: styleConfig.color.text.white,
            fontWeight: 600,
          },
          width: 40,
          height: 40,
          '&.Mui-active': {
            '& .MuiStepIcon-text': {
              fill: styleConfig.color.text.white,
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          minHeight: mobileTokens.control.height.panel,
          minWidth: 0,
          borderRadius: 0,
          padding: '8px 16px',
          transition: `background-color ${transition.fast}, border-color ${transition.fast}, color ${transition.fast}`,
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: 3,
          },
          '&.Mui-disabled': {
            borderColor: border.medium,
            backgroundColor: styleConfig.color.bg.altLightGrey,
            color: styleConfig.color.text.grayOnOffwhite,
            opacity: 1,
          },
        },
        containedPrimary: {
          backgroundColor: styleConfig.color.bg.gold,
          color: styleConfig.color.text.white,
        },
        outlinedPrimary: {
          borderColor: styleConfig.color.bg.gold,
          color: styleConfig.color.text.darkgrey,
          '&&:hover': {
            backgroundColor: styleConfig.color.text.darkgrey,
            color: styleConfig.color.text.white,
            borderColor: styleConfig.color.text.darkgrey,
          },
        },
        textPrimary: {
          color: styleConfig.color.text.darkgrey,
          '&:hover': {
            backgroundColor: styleConfig.color.bg.offwhite,
            color: styleConfig.color.text.gold,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          minHeight: mobileTokens.control.height.panel,
          borderRadius: 0,
          borderColor: styleConfig.color.bg.gold,
          color: styleConfig.color.text.darkgrey,
          fontFamily: styleConfig.fonts.body,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: letterSpacing.wide,
          textTransform: 'uppercase',
          '&.Mui-selected': {
            backgroundColor: styleConfig.color.bg.goldWarm,
            color: styleConfig.color.text.black,
          },
          '&.Mui-selected:hover': {
            backgroundColor: styleConfig.color.bg.goldWarm,
            color: styleConfig.color.text.black,
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: 3,
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          borderColor: styleConfig.color.bg.gold,
          color: styleConfig.color.text.darkgrey,
          fontFamily: styleConfig.fonts.body,
          fontWeight: 600,
        },
        clickable: {
          minHeight: mobileTokens.control.height.panel,
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: 3,
          },
        },
        deleteIcon: {
          color: styleConfig.color.text.gold,
          '&:hover': { color: styleConfig.color.text.darkgrey },
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        severity: 'info',
        variant: 'standard',
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontFamily: styleConfig.fonts.body,
        },
        standardError: ({ theme }) => ({
          border: `1px solid ${border.medium}`,
          borderLeft: `4px solid ${theme.palette.error.main}`,
        }),
        standardWarning: ({ theme }) => ({
          border: `1px solid ${border.medium}`,
          borderLeft: `4px solid ${theme.palette.warning.main}`,
        }),
        standardSuccess: ({ theme }) => ({
          border: `1px solid ${border.medium}`,
          borderLeft: `4px solid ${theme.palette.success.main}`,
        }),
        standardInfo: {
          border: `1px solid ${border.medium}`,
          borderLeft: `4px solid ${styleConfig.color.bg.gold}`,
          backgroundColor: styleConfig.color.bg.offwhite,
          color: styleConfig.color.text.darkgrey,
          '& .MuiAlert-icon': { color: styleConfig.color.text.gold },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: shadow.dialog,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: mobileTokens.control.height.panel,
          borderRadius: 0,
          color: styleConfig.color.text.darkgrey,
          fontFamily: styleConfig.fonts.body,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: letterSpacing.wide,
          textTransform: 'uppercase',
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: -2,
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: styleConfig.color.bg.darkgrey,
          color: styleConfig.color.text.white,
          boxShadow: shadow.panel,
          fontFamily: styleConfig.fonts.body,
        },
      },
    },
    MuiPagination: {
      defaultProps: {
        shape: 'rounded',
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          minWidth: mobileTokens.control.height.panel,
          height: mobileTokens.control.height.panel,
          borderRadius: 0,
          fontFamily: styleConfig.fonts.body,
          fontWeight: 600,
          '&.Mui-selected': {
            backgroundColor: styleConfig.color.bg.gold,
            color: styleConfig.color.text.white,
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${styleConfig.color.bg.gold}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: shadow.panel,
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${border.medium}`,
          boxShadow: shadow.panel,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        alternativeLabel: {
          fontSize: 14,
          '&.Mui-active': {
            fontWeight: 600,
          },
          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: 13,
          },
        },
      },
    },
  },
});

export default theme;
